import { Checkbox, Radio, Link as MaterialLink } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { Mentor, MentorId } from '../../api/mentors';
import { VentureId } from '../../api/ventures/types/Venture';
import { getRoutePath, Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { Text, Link } from '../common';
import { MentorManagementRequestIconButton } from './mentor-management-request-button';
import { RemoveAssignmentButton } from './remove-assignment-modal';

export interface AssignmentItem extends Mentor {
  name: string;
  assignmentId: string;
  isPrimary: boolean;
}

interface AssignedTableProps {
  className?: string;
  assignments: AssignmentItem[];
  disabledAssignmentsIds: Record<string, boolean>;
  multi?: boolean;
  onSelect: (assignment: AssignmentItem, state: boolean) => any;
  onRemove: (assignment: AssignmentItem, comment: string) => Promise<void>;
  readOnly: boolean;
  manageMentorMode?: boolean;
  ventureId?: VentureId;
}

const useStyles = makeStyles({
  container: { maxWidth: 560 },
  row: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    padding: '11px 12px 11px 0',
    '&:hover:not($headRow)': { background: COLORS.COLOR_GRAY_LIGHTENED_45 },
  },
  headRow: {
    padding: '13px 12px 15px 0',
    '& $leadTd': { padding: '0 16px' },
  },
  disabledRow: {
    background: COLORS.COLOR_GRAY_LIGHTENED_45,
    '& $text': { color: COLORS.COLOR_TEXT_LIGHTENED_20 },
  },
  td: { padding: '0 10px', boxSizing: 'border-box' },
  leadTd: { minWidth: 70 },
  iconTd: { width: 40 },
  nameTd: {
    width: '100%',
    minHeight: 40,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
  },
  text: { color: COLORS.COLOR_BLUE_DARKENED_10 },
  link: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

function AssignedTable({
  className,
  assignments,
  disabledAssignmentsIds,
  multi = true,
  onSelect,
  onRemove,
  readOnly = true,
  manageMentorMode = false,
  ventureId,
}: AssignedTableProps) {
  const classes = useStyles();
  const location = useLocation();

  const hasMentorManagement = manageMentorMode && ventureId;
  const hasRemoveAssignment = !readOnly;
  const hasLead = !readOnly;

  const currentPath = useMemo(
    () => `${location.pathname}${location.search}`,
    [location.pathname, location.search],
  );

  const handleSelect = useCallback(
    (assignment: AssignmentItem) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onSelect(assignment, e.target.checked);
    },
    [onSelect],
  );

  const handleRemove = useCallback(
    (assignment: AssignmentItem) => async (notes: string) => {
      await onRemove(assignment, notes);
    },
    [onRemove],
  );

  const stopPropagation = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div className={cn(classes.container, className)}>
      <div className={cn(classes.row, classes.headRow)}>
        {hasLead && (
          <div className={cn(classes.td, classes.leadTd)}>
            <Text variant='normal2' bold>
              Lead
            </Text>
          </div>
        )}
        <div className={cn(classes.td, classes.nameTd)}>
          <Text variant='normal2' bold>
            Name
          </Text>
        </div>
        {hasMentorManagement && (
          <div className={cn(classes.td, classes.iconTd)} />
        )}
        {hasRemoveAssignment && (
          <div className={cn(classes.td, classes.iconTd)} />
        )}
      </div>

      {assignments.map((assignment, index) => {
        const { assignmentId, isPrimary, id, name } = assignment;
        const isDisabled = !!disabledAssignmentsIds[assignmentId];

        return (
          <div
            key={assignmentId}
            data-testid={`assigned-mentor-${index}`}
            className={cn(classes.row, { [classes.disabledRow]: isDisabled })}>
            {hasLead && (
              <div className={cn(classes.td, classes.leadTd)}>
                {multi ? (
                  <Checkbox
                    checked={isPrimary}
                    onChange={handleSelect(assignment)}
                    color='primary'
                    disabled={isDisabled}
                  />
                ) : (
                  <Radio
                    name='assigned-table'
                    value={assignmentId}
                    checked={isPrimary}
                    onChange={handleSelect(assignment)}
                    color='primary'
                    disabled={isDisabled}
                  />
                )}
              </div>
            )}

            <div className={cn(classes.td, classes.nameTd)}>
              <MaterialLink
                onClick={stopPropagation}
                component={Link}
                className={classes.link}
                to={{
                  pathname: getRoutePath(Pages.MENTOR_DETAILS, {
                    mentorId: id,
                  }),
                  state: { prevPath: currentPath },
                }}>
                <Text className={classes.text} variant='normal'>
                  {readOnly ? `${name}${isPrimary ? ' (Lead)' : ''}` : name}
                </Text>
              </MaterialLink>
            </div>

            {hasMentorManagement && (
              <MentorManagementRequestIconButton
                title={`Manage ${name}`}
                mentorId={id as MentorId}
                ventureId={ventureId}
                defaultType='REMOVE'
                selectableType
              />
            )}

            {hasRemoveAssignment && (
              <div>
                <RemoveAssignmentButton
                  isDisabled={isDisabled}
                  testId={`assigned-delete-button-${index}`}
                  onRemove={handleRemove(assignment)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default AssignedTable;
